import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { ChevronUp, ChevronDown, Sparkles, Coins } from 'lucide-react';
import CalcSectionHeader from './CalcSectionHeader';
import AIButton from './AIButton';

const DirectCosts = ({
 showCOGS,
 setShowCOGS,
 formData,
 setFormData,
 numTrays,
 calculationMethod,
 wetWeightPerTray,
 dryWeightPerTray,
 piecesPerTray,
 aiEstimatedFields,
 openAIDialog,
 openPieceCostCalculator,
 handleIngredientCostPerLb,
 handleIngredientWeight,
 handlePackagingItemChange,
 handleElectricityCostPerHour,
 handleConsumableItemChange,
 handleLaborPhaseChange,
 askAIForPowerRate,
 askAIForMachinePower,
 aiLoading
}) => {
 
 // Calculate total packaging cost from individual items
 const calculatePackagingTotal = () => {
 const bag = parseFloat(formData.packaging_bag_cost) || 0;
 const label = parseFloat(formData.packaging_label_cost) || 0;
 const o2 = parseFloat(formData.packaging_o2_cost) || 0;
 const seal = parseFloat(formData.packaging_seal_cost) || 0;
 const other = parseFloat(formData.packaging_other_cost) || 0;
 
 const perUnit = bag + label + o2 + seal + other;
 const units = parseFloat(formData.units_per_batch) || 0;
 const total = perUnit * units;
 
 return { perUnit: perUnit.toFixed(2), total: total.toFixed(2) };
 };

 // Calculate total labor cost from phases
 const calculateLaborTotal = () => {
 const prepHours = parseFloat(formData.labor_prep_hours) || 0;
 const finishHours = parseFloat(formData.labor_finish_hours) || 0;
 const packHours = parseFloat(formData.labor_pack_hours) || 0;
 const rate = parseFloat(formData.labor_rate) || 0;
 
 const totalHours = prepHours + finishHours + packHours;
 const totalCost = totalHours * rate;
 
 return { totalHours: totalHours.toFixed(2), totalCost: totalCost.toFixed(2) };
 };

 // Calculate total consumables from items
 const calculateConsumablesTotal = () => {
 const pumpOil = parseFloat(formData.consumable_pump_oil) || 0;
 const trayLiners = parseFloat(formData.consumable_tray_liners) || 0;
 const cleaningSupplies = parseFloat(formData.consumable_cleaning) || 0;
 const other = parseFloat(formData.consumable_other) || 0;
 
 return (pumpOil + trayLiners + cleaningSupplies + other).toFixed(2);
 };

 const packagingTotals = calculatePackagingTotal();
 const laborTotals = calculateLaborTotal();
 const consumablesTotal = calculateConsumablesTotal();

 // Auto-calculate total weight/quantity from Batch Basics
 const autoCalculatedWeight = () => {
 if (calculationMethod === 'weight') {
 // Use wet weight for ingredients (before freeze-drying)
 const totalWetWeight = (parseFloat(wetWeightPerTray) || 0) * (parseFloat(numTrays) || 0);
 return totalWetWeight.toFixed(2);
 } else {
 // Use total pieces for piece-based calculation
 const totalPieces = (parseFloat(piecesPerTray) || 0) * (parseFloat(numTrays) || 0);
 return totalPieces.toFixed(0);
 }
 };

 const calculatedWeight = autoCalculatedWeight();
 
 // Calculate ingredient cost per tray
 const ingredientCostPerTray = ((parseFloat(formData.ingredient_cost) || 0) / (parseFloat(numTrays) || 1)).toFixed(2);

 return (
 <Card className="border border-border rounded-2xl overflow-hidden">
 <CalcSectionHeader
 icon={Coins}
 title="Direct Costs (COGS)"
 description="Ingredients, packaging, labor, energy — anything that scales per batch"
 expanded={showCOGS}
 onToggle={() => setShowCOGS(!showCOGS)}
 testId="section-header-costs"
 />
 {showCOGS && (
 <CardContent className="space-y-6 pt-5">
 {/* Ingredient Cost with Unit Selector */}
 <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg space-y-3 border border-blue-200 dark:border-blue-800">
 <p className="text-sm font-semibold text-blue-900 dark:text-blue-100">🥫 Ingredient Cost</p>
 
 <div className="space-y-3">
 <div>
 <Label className="text-xs font-semibold mb-2 block">Cost Unit</Label>
 <div className="grid grid-cols-4 gap-2">
 {['$/lb', '$/oz', '$/kg', '$/g'].map(unit => (
 <label key={unit} className={`flex items-center justify-center gap-1 p-2 border rounded cursor-pointer text-xs ${formData.ingredient_cost_unit === unit ? 'border-blue-500 bg-blue-100 dark:bg-blue-900/40' : 'border-slate-200 dark:border-slate-700'}`}>
 <input
 type="radio"
 name="ingredient-cost-unit"
 checked={formData.ingredient_cost_unit === unit}
 onChange={() => setFormData(prev => ({ ...prev, ingredient_cost_unit: unit }))}
 className="w-3 h-3"
 />
 <span>{unit}</span>
 </label>
 ))}
 </div>
 </div>

 {calculationMethod === 'weight' && (
 <div>
 <Label className="text-xs font-semibold mb-2 block">Weight Unit (from Batch Basics)</Label>
 <div className="grid grid-cols-4 gap-2">
 {['lbs', 'oz', 'kg', 'g'].map(unit => (
 <label key={unit} className={`flex items-center justify-center gap-1 p-2 border rounded cursor-pointer text-xs ${formData.ingredient_weight_unit === unit ? 'border-blue-500 bg-blue-100 dark:bg-blue-900/40' : 'border-slate-200 dark:border-slate-700'}`}>
 <input
 type="radio"
 name="ingredient-weight-unit"
 checked={formData.ingredient_weight_unit === unit}
 onChange={() => setFormData(prev => ({ ...prev, ingredient_weight_unit: unit }))}
 className="w-3 h-3"
 />
 <span>{unit}</span>
 </label>
 ))}
 </div>
 </div>
 )}
 </div>
 
 <div className="grid grid-cols-2 gap-3">
 <div className="space-y-2">
 <div className="flex items-center justify-between">
 <Label className="text-sm">Cost {formData.ingredient_cost_unit || '$/lb'}</Label>
 <Button
 variant="ghost"
 size="sm"
 onClick={openPieceCostCalculator}
 className="h-6 text-xs text-blue-600 hover:text-blue-700"
 >
 🍬 Piece Calculator
 </Button>
 </div>
 <Input
 type="number"
 step="0.01"
 value={formData.ingredient_cost_per_lb}
 onChange={(e) => handleIngredientCostPerLb(e.target.value)}
 placeholder="5.00"
 data-testid="input-ingredient_cost_per_lb"
 />
 </div>
 <div className="space-y-2">
 <Label className="text-sm">Total Weight/Qty (Auto-calculated)</Label>
 <Input
 type="text"
 value={`${calculatedWeight} ${calculationMethod === 'weight' ? (formData.ingredient_weight_unit || 'lbs') : 'pieces'}`}
 readOnly
 className="bg-slate-100 dark:bg-slate-800 cursor-not-allowed"
 placeholder="0"
 />
 <p className="text-xs text-muted-foreground">
 {calculationMethod === 'weight' 
 ? `${wetWeightPerTray || '0'} ${formData.ingredient_weight_unit || 'lbs'}/tray × ${numTrays || '0'} trays`
 : `${piecesPerTray || '0'} pieces/tray × ${numTrays || '0'} trays`
 }
 </p>
 </div>
 </div>
 
 <div className="pt-2 border-t border-blue-200 dark:border-blue-700 space-y-1">
 {calculationMethod === 'weight' && formData.ingredient_cost_unit && formData.ingredient_weight_unit && (
 <div className="text-xs text-blue-700 dark:text-blue-300 bg-blue-100 dark:bg-blue-900/40 p-2 rounded mb-2">
 💡 Calculation: {calculatedWeight} {formData.ingredient_weight_unit} × ${formData.ingredient_cost_per_lb || '0'}/{formData.ingredient_cost_unit.replace('$/', '')} = ${formData.ingredient_cost || '0.00'}
 </div>
 )}
 <div className="flex justify-between text-xs text-muted-foreground">
 <span>Per Tray:</span>
 <span>${ingredientCostPerTray}</span>
 </div>
 <div className="flex justify-between items-center">
 <span className="text-sm font-semibold">Total Per Run:</span>
 <span className="text-lg font-bold text-blue-600">
 ${formData.ingredient_cost || '0.00'}
 </span>
 </div>
 </div>
 </div>

 {/* Packaging Cost - Detailed Breakdown */}
 <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg space-y-3 border border-green-200 dark:border-green-800">
 <div className="flex items-start justify-between gap-3">
 <div>
 <p className="text-sm font-semibold text-green-900 dark:text-green-100">📦 Packaging Cost Breakdown</p>
 <p className="text-xs text-muted-foreground">Cost per individual unit/package</p>
 </div>
 <Button
 variant="outline"
 size="sm"
 onClick={() => window.open('/store', '_blank')}
 className="shrink-0 bg-blue-50 hover:bg-blue-100 border-blue-300 text-blue-700 dark:bg-blue-900/20 dark:hover:bg-blue-900/40 dark:border-blue-700 dark:text-blue-300"
 >
 🛒 Check Store Pricing
 </Button>
 </div>
 
 <div className="grid grid-cols-2 gap-3">
 <div className="space-y-2">
 <Label className="text-xs">Bag/Container ($)</Label>
 <Input
 type="number"
 step="0.01"
 value={formData.packaging_bag_cost}
 onChange={(e) => handlePackagingItemChange('packaging_bag_cost', e.target.value)}
 placeholder="0.50"
 />
 </div>
 <div className="space-y-2">
 <Label className="text-xs">Label ($)</Label>
 <Input
 type="number"
 step="0.01"
 value={formData.packaging_label_cost}
 onChange={(e) => handlePackagingItemChange('packaging_label_cost', e.target.value)}
 placeholder="0.15"
 />
 </div>
 <div className="space-y-2">
 <Label className="text-xs">O2 Absorber ($)</Label>
 <Input
 type="number"
 step="0.01"
 value={formData.packaging_o2_cost}
 onChange={(e) => handlePackagingItemChange('packaging_o2_cost', e.target.value)}
 placeholder="0.05"
 />
 </div>
 <div className="space-y-2">
 <Label className="text-xs">Seal/Tape ($)</Label>
 <Input
 type="number"
 step="0.01"
 value={formData.packaging_seal_cost}
 onChange={(e) => handlePackagingItemChange('packaging_seal_cost', e.target.value)}
 placeholder="0.10"
 />
 </div>
 <div className="space-y-2 col-span-2">
 <Label className="text-xs">Other Packaging ($)</Label>
 <Input
 type="number"
 step="0.01"
 value={formData.packaging_other_cost}
 onChange={(e) => handlePackagingItemChange('packaging_other_cost', e.target.value)}
 placeholder="0.00"
 />
 </div>
 </div>
 
 <div className="pt-2 border-t border-green-200 dark:border-green-700 space-y-1">
 <div className="flex justify-between text-xs text-muted-foreground">
 <span>Cost per Unit:</span>
 <span>${packagingTotals.perUnit}</span>
 </div>
 <div className="flex justify-between text-xs text-muted-foreground">
 <span>Units per Batch:</span>
 <span>{formData.units_per_batch || '0'}</span>
 </div>
 <div className="flex justify-between text-xs text-muted-foreground">
 <span>Per Tray:</span>
 <span>${((parseFloat(packagingTotals.total) || 0) / (parseFloat(numTrays) || 1)).toFixed(2)}</span>
 </div>
 <div className="flex justify-between items-center">
 <span className="text-sm font-semibold">Total Per Run:</span>
 <span className="text-lg font-bold text-green-600">
 ${packagingTotals.total}
 </span>
 </div>
 </div>
 </div>

 {/* Labor Cost - 3 Phases */}
 <div className="bg-primary/5 dark:bg-primary/20/20 p-4 rounded-lg space-y-3 border border-primary/30 dark:border-primary/30">
 <p className="text-sm font-semibold text-primary dark:text-primary">👤 Labor Cost Breakdown</p>
 
 <div className="space-y-3">
 <div className="grid grid-cols-2 gap-3">
 <div className="col-span-2">
 <AIButton 
 formData={formData} 
 setFormData={setFormData} 
 aiEstimatedFields={aiEstimatedFields} 
 openAIDialog={openAIDialog} 
 fieldName="labor_rate" 
 label="Hourly Rate ($/hr)" 
 />
 </div>
 </div>
 
 <div className="space-y-3 pt-2 border-t border-primary/30 dark:border-primary/30">
 <div className="space-y-2">
 <Label className="text-xs font-semibold">Batch Preparation & Loading (hours)</Label>
 <Input
 type="number"
 step="0.25"
 value={formData.labor_prep_hours}
 onChange={(e) => handleLaborPhaseChange('labor_prep_hours', e.target.value)}
 placeholder="1.5"
 />
 <p className="text-xs text-muted-foreground">Washing, slicing, arranging on trays, loading machine</p>
 </div>
 
 <div className="space-y-2">
 <Label className="text-xs font-semibold">Batch Finishing & Unloading (hours)</Label>
 <Input
 type="number"
 step="0.25"
 value={formData.labor_finish_hours}
 onChange={(e) => handleLaborPhaseChange('labor_finish_hours', e.target.value)}
 placeholder="0.5"
 />
 <p className="text-xs text-muted-foreground">Unloading trays, quality check, cleaning machine</p>
 </div>
 
 <div className="space-y-2">
 <Label className="text-xs font-semibold">Packaging (hours)</Label>
 <Input
 type="number"
 step="0.25"
 value={formData.labor_pack_hours}
 onChange={(e) => handleLaborPhaseChange('labor_pack_hours', e.target.value)}
 placeholder="1.0"
 />
 <p className="text-xs text-muted-foreground">Bagging, labeling, sealing, final packaging</p>
 </div>
 </div>
 </div>
 
 <div className="pt-2 border-t border-primary/30 dark:border-primary/30 space-y-1">
 <div className="flex justify-between text-xs text-muted-foreground">
 <span>Total Hours:</span>
 <span>{laborTotals.totalHours} hrs</span>
 </div>
 <div className="flex justify-between text-xs text-muted-foreground">
 <span>Per Tray:</span>
 <span>${((parseFloat(laborTotals.totalCost) || 0) / (parseFloat(numTrays) || 1)).toFixed(2)}</span>
 </div>
 <div className="flex justify-between items-center">
 <span className="text-sm font-semibold">Total Per Run:</span>
 <span className="text-lg font-bold text-primary">
 ${laborTotals.totalCost}
 </span>
 </div>
 </div>
 </div>

 {/* Electricity - with AI Power Rate Lookup */}
 <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg space-y-3 border border-yellow-200 dark:border-yellow-800">
 <p className="text-sm font-semibold text-yellow-900 dark:text-yellow-100">⚡ Electricity Cost</p>
 
 <div className="grid grid-cols-2 gap-3">
 <div className="space-y-2">
 <Label className="text-xs">kWh Rate ($/kWh)</Label>
 <div className="flex gap-1">
 <Input
 type="number"
 step="0.001"
 value={formData.electricity_rate_kwh}
 onChange={(e) => handleElectricityCostPerHour(e.target.value, 'electricity_rate_kwh')}
 placeholder="0.12"
 className={aiEstimatedFields.electricity_rate_kwh ? 'border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20' : ''}
 />
 <Button
 type="button"
 variant="ghost"
 size="sm"
 onClick={askAIForPowerRate}
 disabled={aiLoading}
 className="h-9 px-2"
 title="Ask AI for local rate"
 >
 {aiLoading ? <Sparkles className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
 </Button>
 </div>
 {aiEstimatedFields.electricity_rate_kwh && (
 <p className="text-xs text-yellow-700 dark:text-yellow-300">
 <Sparkles className="w-3 h-3 inline mr-1" />
 {aiEstimatedFields.electricity_rate_kwh.explanation}
 </p>
 )}
 </div>
 
 <div className="space-y-2">
 <Label className="text-xs">Machine Power Draw (kW)</Label>
 <div className="flex gap-1">
 <Input
 type="number"
 step="0.1"
 value={formData.machine_power_kw}
 onChange={(e) => handleElectricityCostPerHour(e.target.value, 'machine_power_kw')}
 placeholder="1.5"
 className={aiEstimatedFields.machine_power_kw ? 'border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20' : ''}
 />
 <Button
 type="button"
 variant="ghost"
 size="sm"
 onClick={askAIForMachinePower}
 disabled={aiLoading}
 className="h-9 px-2"
 title="Ask AI for your machine's power"
 >
 {aiLoading ? <Sparkles className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
 </Button>
 </div>
 {aiEstimatedFields.machine_power_kw && (
 <p className="text-xs text-yellow-700 dark:text-yellow-300">
 <Sparkles className="w-3 h-3 inline mr-1" />
 {aiEstimatedFields.machine_power_kw.explanation}
 </p>
 )}
 <p className="text-xs text-muted-foreground">Typical: 1.5-2.5 kW</p>
 </div>
 </div>
 
 <div className="bg-yellow-100 dark:bg-yellow-900/40 p-2 rounded text-xs">
 <p className="font-semibold mb-1">💡 Calculation:</p>
 <p>{formData.electricity_rate_kwh || '0'} $/kWh × {formData.machine_power_kw || '0'} kW × {formData.cycle_time_hours || '24'} hrs = ${formData.electricity_cost || '0.00'}</p>
 </div>
 
 <div className="pt-2 border-t border-yellow-200 dark:border-yellow-700 space-y-1">
 <div className="flex justify-between text-xs text-muted-foreground">
 <span>Per Tray:</span>
 <span>${((parseFloat(formData.electricity_cost) || 0) / (parseFloat(numTrays) || 1)).toFixed(2)}</span>
 </div>
 <div className="flex justify-between items-center">
 <span className="text-sm font-semibold">Total Per Run:</span>
 <span className="text-lg font-bold text-yellow-600">
 ${formData.electricity_cost || '0.00'}
 </span>
 </div>
 </div>
 </div>

 {/* Consumables - Detailed Breakdown */}
 <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg space-y-3 border border-orange-200 dark:border-orange-800">
 <div className="flex items-start justify-between gap-3">
 <p className="text-sm font-semibold text-orange-900 dark:text-orange-100">🔧 Consumables per Batch</p>
 <Button
 variant="outline"
 size="sm"
 onClick={() => window.open('/store', '_blank')}
 className="shrink-0 bg-blue-50 hover:bg-blue-100 border-blue-300 text-blue-700 dark:bg-blue-900/20 dark:hover:bg-blue-900/40 dark:border-blue-700 dark:text-blue-300"
 >
 🛒 Check Store Pricing
 </Button>
 </div>
 
 <div className="grid grid-cols-2 gap-3">
 <div className="space-y-2">
 <Label className="text-xs">Pump Oil ($)</Label>
 <Input
 type="number"
 step="0.01"
 value={formData.consumable_pump_oil}
 onChange={(e) => handleConsumableItemChange('consumable_pump_oil', e.target.value)}
 placeholder="2.00"
 />
 </div>
 <div className="space-y-2">
 <Label className="text-xs">Tray Liners ($)</Label>
 <Input
 type="number"
 step="0.01"
 value={formData.consumable_tray_liners}
 onChange={(e) => handleConsumableItemChange('consumable_tray_liners', e.target.value)}
 placeholder="1.50"
 />
 </div>
 <div className="space-y-2">
 <Label className="text-xs">Cleaning Supplies ($)</Label>
 <Input
 type="number"
 step="0.01"
 value={formData.consumable_cleaning}
 onChange={(e) => handleConsumableItemChange('consumable_cleaning', e.target.value)}
 placeholder="1.00"
 />
 </div>
 <div className="space-y-2">
 <Label className="text-xs">Other Consumables ($)</Label>
 <Input
 type="number"
 step="0.01"
 value={formData.consumable_other}
 onChange={(e) => handleConsumableItemChange('consumable_other', e.target.value)}
 placeholder="0.50"
 />
 </div>
 </div>
 
 <div className="pt-2 border-t border-orange-200 dark:border-orange-700 space-y-1">
 <div className="flex justify-between text-xs text-muted-foreground">
 <span>Per Tray:</span>
 <span>${((parseFloat(consumablesTotal) || 0) / (parseFloat(numTrays) || 1)).toFixed(2)}</span>
 </div>
 <div className="flex justify-between items-center">
 <span className="text-sm font-semibold">Total Per Run:</span>
 <span className="text-lg font-bold text-orange-600">
 ${consumablesTotal}
 </span>
 </div>
 </div>
 </div>

 {/* Waste/Loss */}
 <div className="pt-4 border-t">
 <AIButton 
 formData={formData} 
 setFormData={setFormData} 
 aiEstimatedFields={aiEstimatedFields} 
 openAIDialog={openAIDialog} 
 fieldName="waste_loss" 
 label="Waste/Loss Allowance ($)" 
 />
 </div>

 {/* TOTAL COGS SUMMARY */}
 <div className="mt-6 p-4 bg-pro-soft from-slate-100 to-slate-50 dark:from-slate-800 dark:to-slate-900 rounded-lg border-2 border-slate-300 dark:border-slate-700">
 <p className="text-sm font-semibold mb-3">💰 Total Direct Costs (COGS)</p>
 <div className="grid grid-cols-2 gap-4">
 <div className="text-center p-3 bg-white dark:bg-slate-800 rounded border">
 <p className="text-xs text-muted-foreground mb-1">Per Tray</p>
 <p className="text-2xl font-bold text-slate-700 dark:text-slate-300">
 ${(
 ((parseFloat(formData.ingredient_cost) || 0) +
 (parseFloat(packagingTotals.total) || 0) +
 (parseFloat(laborTotals.totalCost) || 0) +
 (parseFloat(formData.electricity_cost) || 0) +
 (parseFloat(consumablesTotal) || 0) +
 (parseFloat(formData.waste_loss) || 0)) / (parseFloat(numTrays) || 1)
 ).toFixed(2)}
 </p>
 </div>
 <div className="text-center p-3 bg-white dark:bg-slate-800 rounded border">
 <p className="text-xs text-muted-foreground mb-1">Per Run</p>
 <p className="text-2xl font-bold text-green-600">
 ${(
 (parseFloat(formData.ingredient_cost) || 0) +
 (parseFloat(packagingTotals.total) || 0) +
 (parseFloat(laborTotals.totalCost) || 0) +
 (parseFloat(formData.electricity_cost) || 0) +
 (parseFloat(consumablesTotal) || 0) +
 (parseFloat(formData.waste_loss) || 0)
 ).toFixed(2)}
 </p>
 </div>
 </div>
 </div>
 </CardContent>
 )}
 </Card>
 );
};

export default DirectCosts;
